import React, { useState, useEffect, useRef } from "react";
import { navigateTo } from "../../utils/navigationUtils";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IoMenu, IoArrowForward, IoSunny } from "react-icons/io5";
import { FaTimes, FaMoon } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import {
  selectIsLoggedIn,
  setLoginData,
} from "../../redux/slices/user/userSlice";
import { FaUserLarge } from "react-icons/fa6";
import PendingServices from "../pendingService/PendingServices";

const Header = ({ isDark, setIsDark }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const [activeRoute, setActiveRoute] = useState(location.pathname);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const MotionLink = motion(Link);

  const pathLang = location.pathname.split("/")[1];
  const resolvedLang = ["am", "ru", "en"].includes(pathLang)
    ? pathLang
    :  "am" || i18n.language;

  useEffect(() => {
      i18n.changeLanguage(resolvedLang);
      localStorage.setItem("i18nextLng", resolvedLang);
      localStorage.setItem("language", resolvedLang);
    if (!["am", "ru", "en"].includes(pathLang)) {
      navigate(`/${resolvedLang}${location.pathname}`);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const logOut = () => {
    localStorage.removeItem("userData");
    setUserMenuOpen(false);
    dispatch(setLoginData(null));
    window.location.replace(`/${resolvedLang}/`);
  };

  const navbar = [
    { name: "home", route: "/" },
    { name: "services", route: "/services" },
    { name: "partners", route: "/partners" },
    { name: "aboutUs", route: "/about" },
    { name: "contactUS", route: "/contact" },
  ];

  const handleNavigation = (route) => {
    navigateTo(navigate, route);
    setActiveRoute(route);
    setMenuOpen(false);
    document.body.classList.remove("menu-open");
  };

  const handleGetStarted = () => {
    navigate(`/${resolvedLang}/login`);
  };

  const toggleDarkMode = () => {
    setIsDark(!isDark);
  };

  const changeLanguage = (lng) => {
    if (lng === resolvedLang) return;

    const pathParts = location.pathname.split("/");

    if (["am", "ru", "en"].includes(pathParts[1])) {
      pathParts[1] = lng;
    } else {
      pathParts.splice(1, 0, lng);
    }

    const newPath = pathParts.join("/") || `/${lng}`;

    i18n.changeLanguage(lng);
    localStorage.setItem("i18nextLng", lng);
    localStorage.setItem("language", lng);

    navigate(newPath);
  };

  useEffect(() => {
    if (menuOpen) {
      document.body.classList.add("menu-open");
    } else {
      document.body.classList.remove("menu-open");
    }
  }, [menuOpen]);

  const maskEmail = (email) => {
    if (!email) return "";

    const [name, domain] = email.split("@");

    if (name.length <= 6) {
      return `${name}***@${domain}`;
    }

    return `${name.slice(0, 6)}***@${domain}`;
  };

  return (
    <>
      {/* Backdrop Blur */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-md z-40"
            onClick={() => setMenuOpen(false)}
          />
        )}
      </AnimatePresence>

      <header className="w-[95%] rounded-[50px] md:w-[800px] lg:w-[800px] shadow-2xl sticky top-5 z-50 mx-auto transition-all duration-500">
        <div className="flex relative justify-between items-center rounded-[50px] px-4 lg:px-5 h-[70px]">
          {/* Logo */}
          {/* Background image */}
          <img
            src="/assets/OrangeBackground.png"
            alt=""
            className="absolute inset-0 w-full h-full object-cover brightness(0.8) overflow-hidden rounded-[50px]"
          />

          {/* Gradient overlay */}
          <div
            className="absolute inset-0 pointer-events-none rounded-[50px] overflow-hidden"
            style={{
              background:
                "linear-gradient(180deg, rgba(238, 82, 34, 0.92) 100%, rgba(0, 0, 0, 0.0) 0%)",
            }}
          />
          <div
            className="cursor-pointer flex items-center gap-2 relative z-10"
            onClick={() => navigate(`/${resolvedLang}/`)}
          >
            {/* Desktop logo */}
            <img
              src="/assets/header-logo-white.png"
              alt="My Service Logo"
              className="hidden md:block h-[38px] md:h-11 w-auto"
            />

            {/* Mobile logo */}
            <img
              src="/assets/header-logo-white-small.png"
              alt="My Service Logo Mobile"
              className="block md:hidden h-11 w-auto"
            />

            {/* Dark mode logo */}
            {/* <img
              src="/assets/header-logo-black.png"
              alt="My Service Logo Dark"
              className="h-11 w-auto hidden dark:block"
            /> */}
          </div>

          {/* Right side: Get Started + Burger */}
          <div className="flex items-center gap-3 relative z-10">
            {isLoggedIn ? (
              <div className="relative flex items-center gap-3">
                {/* User Button */}
                <button
                  onClick={() => {
                    setUserMenuOpen((prev) => !prev);
                    setMenuOpen(false);
                  }}
                  ref={userMenuRef}
                  className="flex items-center justify-center gap-3 bg-white_bg text-black px-[0px] md:px-[18px] h-[43px] w-[43px] md:w-auto rounded-[50px] transition-all duration-300 hover:bg-black hover:text-white dark:bg-black_bg dark:text-white dark:hover:bg-white dark:hover:text-black"
                >
                  <FaUserLarge className="text-primary text-xl" />

                  <span className="hidden sm:block max-w-[180px] truncate text-[16px]">
                    {maskEmail(isLoggedIn.email)}
                  </span>
                </button>
                <PendingServices onOpen={() => setMenuOpen(false)} />
                {/* Dropdown */}
                <AnimatePresence>
                  {userMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-14 mt-48 w-56 rounded-xl bg-white shadow-xl z-50 text-secondary_black dark:text-white dark:bg-black border border-gray-200 dark:border-gray-700 overflow-hidden"
                    >
                      <button
                        onClick={() => {
                          setUserMenuOpen(false);
                          navigate(`/${resolvedLang}/my-profile/`);
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-secondary_black hover:text-white dark:hover:bg-white dark:hover:text-secondary_black transition text-[18px]"
                      >
                        {t("myProfile")}
                      </button>

                      <button
                        onClick={logOut}
                        className="w-full text-left px-4 py-3 hover:bg-secondary_black hover:text-white dark:hover:bg-white dark:hover:text-secondary_black transition text-[18px]"
                      >
                        {t("signOut")}
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <button
                onClick={handleGetStarted}
                className="flex items-center justify-center gap-[10px] md:gap-5 bg-white_bg text-black px-[20px] py-2 rounded-[50px] font-[400] text-[15px] md:text-[20px] transition-all duration-300 hover:bg-black hover:text-white capitalize dark:bg-black_bg dark:text-white dark:hover:bg-white dark:hover:text-black"
              >
                <span className="order-1">{t("getStarted")}</span>
                <IoArrowForward className="order-2 text-2xl text-primary" />
              </button>
            )}

            {/* Menu Toggle */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-2 bg-white_bg text-primary flex justify-center items-center h-[43px] w-[43px] text-2xl rounded-[50px] duration-300 hover:bg-black hover:text-white transition dark:bg-black_bg dark:text-primary dark:hover:bg-white dark:hover:text-primary"
              aria-label="Open menu"
            >
              {menuOpen ? (
                <FaTimes className="h-7 w-auto transition-transform duration-300 rotate-180" />
              ) : (
                <IoMenu className="h-8 w-auto transition-transform duration-300" />
              )}
            </button>
          </div>
        </div>

        {/* Dropdown Menu */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="absolute left-0 top-20 w-full bg-white/80 dark:bg-black/80 rounded-3xl shadow-2xl overflow-hidden z-50"
              style={{ backdropFilter: "blur(8px)" }}
            >
              <div className="flex flex-col items-center py-6 gap-6 font-[400] text-[25px] md:text-[30px]">
                {navbar.map((item) => {
                  const isActive =
                    location.pathname.replace(/^\/(am|en)/, "") === item.route;

                  return (
                    <MotionLink
                      key={item.route}
                      to={`/${resolvedLang}${item.route}`}
                      rel="noopener noreferrer"
                      initial="rest"
                      whileHover="hover"
                      animate={isActive ? "hover" : "rest"}
                      variants={{
                        rest: { scale: 1 },
                        hover: { scale: 1.1 },
                      }}
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 15,
                      }}
                      className={`relative flex items-center gap-3 transition-colors duration-300 group ml-[35px]
                        ${
                          isActive
                            ? "text-primary dark:text-primary"
                            : "hover:text-primary dark:hover:text-primary dark:text-white"
                        }
                      `}
                      onClick={() => setMenuOpen(false)}
                    >
                      {t(item.name)}

                      {/* Arrow icon */}
                      <motion.span
                        variants={{
                          rest: {
                            opacity: 0,
                            rotate: -45,
                            x: -5,
                            y: 5,
                            scale: 0.8,
                          },
                          hover: {
                            opacity: 1,
                            rotate: -45,
                            x: 0,
                            y: 0,
                            scale: 1.5,
                          },
                        }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        className="inline-block text-primary text-[22px]"
                      >
                        <IoArrowForward />
                      </motion.span>
                    </MotionLink>
                  );
                })}

                {/* Language Switch */}
                <div className="flex items-center gap-4 mt-5 text-[18px]">
                  {["am", "ru", "en"].map((lng) => (
                    <button
                      key={lng}
                      onClick={() => changeLanguage(lng)}
                      className={`px-5 py-1 rounded-full border transition-all
                      ${
                        resolvedLang === lng
                          ? "bg-primary text-white border-primary"
                          : `
                            border-gray-300
                            text-gray-900 dark:text-white
                            hover:bg-gray-100
                            dark:hover:bg-white
                            dark:hover:text-black
                          `
                      }
                  `}
                    >
                      {lng === "am" ? "Հայ" : lng === "ru" ? "Рус" : "Eng"}
                    </button>
                  ))}
                </div>

                {/* Dark Mode Switch */}
                <div className="mt-6 flex items-center gap-3">
                  <IoSunny className="text-primary text-xl" />

                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isDark}
                      onChange={toggleDarkMode}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 rounded-full border border-gray-400/50 peer-checked:bg-primary peer-focus:ring-2 peer-focus:ring-primary/50 peer-focus:border-primary transition-all duration-300"></div>
                    <div className="absolute left-[2px] top-[2px] bg-white w-5 h-5 rounded-full shadow transition-all duration-300 peer-checked:translate-x-[22px]"></div>
                  </label>

                  <FaMoon className="text-primary text-xl" />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};

export default Header;
